import React, { useState, useEffect, Fragment, useRef } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import AppPagination from "../Common/AppPagination";
import { Row, Col, Card, Table, Badge, Dropdown, Modal, Button } from "react-bootstrap";
import api, { getFileUrl as getApiFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import swal from "sweetalert";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SignatureModal from "../Common/SignatureModal";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";
import PortalMenu from "../Common/PortalMenu";

const TDSList = ({ workOrderId: propWorkOrderId, isTab = false }) => {
    const user = useSelector((state) => state.auth?.userDetails || {});
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialProject = searchParams.get("project") || "";
    const initialWorkOrder = propWorkOrderId || searchParams.get("workOrder") || "";
    const debounceRef = useRef(null);

    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [projects, setProjects] = useState([]);
    const [showSignModal, setShowSignModal] = useState(false);
    const [approvingId, setApprovingId] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNext: false,
        hasPrev: false,
    });

    const [filters, setFilters] = usePersistentFilters("tds_list_filters", {
        search: "",
        project: initialProject,
        workOrder: initialWorkOrder,
        status: "",
        fromDate: "",
        toDate: "",
        page: 1,
        limit: 10,
    });

    const getListQueryString = () => {
        const params = new URLSearchParams();
        if (filters.workOrder) params.set("workOrder", filters.workOrder);
        return params.toString() ? `?${params.toString()}` : "";
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        const projectFromParams = searchParams.get("project") || "";
        const workOrderFromParams = propWorkOrderId || searchParams.get("workOrder") || "";

        setFilters((prev) => ({
            ...prev,
            project: projectFromParams,
            workOrder: workOrderFromParams,
            page: 1,
        }));
    }, [searchParams, propWorkOrderId]);

    const fetchProjects = async () => {
        try {
            const res = await api.get("/work-orders", { params: { limit: 1000 } });
            if (res.data.success) {
                const list = res.data.data || [];
                setProjects(list);
                if (!initialProject && initialWorkOrder) {
                    const matched = list.find((w) => w._id === initialWorkOrder);
                    if (matched?.project?._id) {
                        setFilters((prev) => ({ ...prev, project: matched.project._id }));
                    }
                }
            }
        } catch (err) {
            console.error("Failed to load projects", err);
        }
    };

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchDocuments();
        }, 400);
        return () => clearTimeout(debounceRef.current);
    }, [
        filters.search,
        filters.project,
        filters.workOrder,
        filters.status,
        filters.fromDate,
        filters.toDate,
        filters.page,
    ]);

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            const params = {
                type: "TDS",
                search: filters.search,
                project: filters.project,
                workOrder: filters.workOrder,
                status: filters.status,
                fromDate: filters.fromDate,
                toDate: filters.toDate,
                page: filters.page,
                limit: filters.limit,
            };

            const response = await api.get("/document-control", { params });

            if (response.data.success) {
                setDocuments(response.data.data || []);
                setPagination(
                    response.data.pagination || {
                        currentPage: 1,
                        totalPages: 1,
                        totalCount: 0,
                        hasNext: false,
                        hasPrev: false,
                    },
                );
            } else {
                setDocuments([]);
                setPagination({
                    currentPage: 1,
                    totalPages: 1,
                    totalCount: 0,
                    hasNext: false,
                    hasPrev: false,
                });
            }
        } catch (error) {
            console.error("Error fetching TDS documents:", error);
            setDocuments([]);
            setPagination({
                currentPage: 1,
                totalPages: 1,
                totalCount: 0,
                hasNext: false,
                hasPrev: false,
            });
        } finally {
            setLoading(false);
        }
    };
    const getApprovalDuration = (submittedDate, approvedDate) => {
        if (!submittedDate) return "—";

        // If not approved yet
        if (!approvedDate) return "Pending";

        const submitted = new Date(submittedDate);
        const approved = new Date(approvedDate);

        const diffTime = approved - submitted;

        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        return `${diffDays} day${diffDays !== 1 ? "s" : ""}`;
    };
    const handleStatusChange = async (id, status) => {
        if (status === "Approved") {
            setApprovingId(id);
            setShowSignModal(true);
            return;
        }

        const confirmed = await swal({
            title: `${status} TDS?`,
            text: `Are you sure you want to mark this TDS as "${status}"?`,
            icon: "warning",
            buttons: true,
        });

        if (!confirmed) return;

        try {
            const response = await api.put(`/document-control/${id}`, { status });
            if (response.data.success) {
                swal("Success", `TDS ${status} successfully!`, "success");
                fetchDocuments();
            }
        } catch (error) {
            swal("Error", "Failed to update status", "error");
        }
    };

    const handleSignatureSave = async (file, stampFile) => {
        const formData = new FormData();
        formData.append("signature", file);
        if (stampFile) {
            formData.append("stamp", stampFile);
        }
        formData.append("status", "Approved");

        try {
            const response = await api.put(
                `/document-control/${approvingId}`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                },
            );
            if (response.data.success) {
                setShowSignModal(false);
                setApprovingId(null);
                swal("Success", "TDS Approved with signature!", "success");
                fetchDocuments();
            }
        } catch (error) {
            swal("Error", "Failed to approve TDS", "error");
        }
    };

    const handleDelete = async (id) => {
        const confirmed = await swal({
            title: "Delete TDS?",
            text: "Are you sure you want to delete this TDS record?",
            icon: "warning",
            buttons: ["Cancel", "Delete"],
            dangerMode: true,
        });

        if (!confirmed) return;

        try {
            const response = await api.delete(`/document-control/${id}`);
            if (response.data.success) {
                swal("Deleted!", "TDS deleted successfully", "success");
                fetchDocuments();
            }
        } catch (error) {
            swal("Error", "Failed to delete record", "error");
        }
    };

    const getFileUrl = (file) => {
        if (!file) return "";
        const url = typeof file === "string" ? file : file.fileUrl;
        return getApiFileUrl(url);
    };

    const handleDocumentDelete = async (docId, fileIndex) => {
        const confirmed = await swal({
            title: "Delete document?",
            text: "This attached document will be removed.",
            icon: "warning",
            buttons: ["Cancel", "Delete"],
            dangerMode: true,
        });

        if (!confirmed) return;

        try {
            const response = await api.delete(`/document-control/${docId}/documents/${fileIndex}`);
            if (response.data.success) {
                setSelectedDoc(response.data.data);
                setDocuments((prev) =>
                    prev.map((item) => (item._id === docId ? response.data.data : item)),
                );
                swal("Deleted!", "Document removed successfully", "success");
            }
        } catch (error) {
            swal("Error", error.response?.data?.message || "Failed to delete document", "error");
        }
    };

    const handlePageChange = (page) => {
        setFilters((prev) => ({ ...prev, page }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case "Approved":
                return "success";
            case "Pending Approval":
                return "warning";
            case "Rejected":
                return "danger";
            default:
                return "secondary";
        }
    };

    const svgMenu = (
        <svg width="20px" height="20px" viewBox="0 0 24 24">
            <circle fill="#000" cx="5" cy="12" r="2"></circle>
            <circle fill="#000" cx="12" cy="12" r="2"></circle>
            <circle fill="#000" cx="19" cy="12" r="2"></circle>
        </svg>
    );

    return (
        <Fragment>
            {!isTab && <PageTitle activeMenu="TDS" motherMenu="Document Control" />}

            {!isTab && (
                <Row className="mb-3">
                    <Col lg={12}>
                        <Card>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={3}>
                                        <label className="form-label">Search Product / WLIN</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Search..."
                                            value={filters.search}
                                            onChange={(e) =>
                                                setFilters({
                                                    ...filters,
                                                    search: e.target.value,
                                                    page: 1,
                                                })
                                            }
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <label className="form-label">Work Order</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={filters.workOrder}
                                            onChange={(e) => {
                                                const selectedWoId = e.target.value;
                                                const matchedWo = projects.find((wo) => wo._id === selectedWoId);
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    workOrder: selectedWoId,
                                                    project: matchedWo?.project?._id || (selectedWoId ? prev.project : ""),
                                                    page: 1,
                                                }));
                                            }}
                                        >
                                            <option value="">All Work Orders</option>
                                            {projects.map((wo) => (
                                                <option key={wo._id} value={wo._id}>
                                                    {wo.woNo} - {wo.project?.projectName}
                                                </option>
                                            ))}
                                        </select>
                                    </Col>
                                    <Col md={2}>
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={filters.status}
                                            onChange={(e) =>
                                                setFilters({
                                                    ...filters,
                                                    status: e.target.value,
                                                    page: 1,
                                                })
                                            }
                                        >
                                            <option value="">All Status</option>
                                            <option value="Approved">Approved</option>
                                            <option value="Pending Approval">Pending Approval</option>
                                            <option value="Rejected">Rejected</option>
                                        </select>
                                    </Col>

                                    <Col md={2}>
                                        <label className="form-label">From Date</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={filters.fromDate}
                                            onChange={(e) =>
                                                setFilters({
                                                    ...filters,
                                                    fromDate: e.target.value,
                                                    page: 1,
                                                })
                                            }
                                        />
                                    </Col>

                                    <Col md={2}>
                                        <label className="form-label">To Date</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={filters.toDate}
                                            onChange={(e) =>
                                                setFilters({
                                                    ...filters,
                                                    toDate: e.target.value,
                                                    page: 1,
                                                })
                                            }
                                        />
                                    </Col>

                                    <Col md={3} className="d-flex align-items-end">
                                        <PermissionWrapper user={user} required={["create:documents"]}>
                                            <Button
                                                variant="primary"
                                                className="w-100 btn-sm"
                                                onClick={() => {
                                                    const params = new URLSearchParams();
                                                    if (filters.workOrder) params.set("workOrder", filters.workOrder);
                                                    navigate(`/tds/upload${params.toString() ? `?${params.toString()}` : ""}`);
                                                }}
                                            >
                                                + Add TDS
                                            </Button>
                                        </PermissionWrapper>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            <Row>
                <Col lg={12}>
                    <Card className="shadow-sm">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table className="mb-0 table-hover align-middle" size="sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-nowrap" style={{ width: "70px" }}>Sr. No.</th>
                                            <th className="text-nowrap" style={{ width: "110px" }}>WLIN</th>
                                            <th style={{ minWidth: "320px" }}>Product Name</th>
                                            <th className="text-nowrap" style={{ width: "200px" }}>WO / PO No.</th>
                                            <th style={{ width: "80px" }}>UOM</th>
                                            <th style={{ width: "80px" }}>QTY</th>
                                            <th className="text-nowrap" style={{ width: "150px" }}>Submitted Date</th>
                                            <th className="text-nowrap" style={{ width: "150px" }}>Approved Date</th>
                                            <th className="text-nowrap" style={{ width: "160px" }}>Approval Duration</th>
                                            <th className="text-nowrap" style={{ width: "160px" }}>Status</th>
                                            <th style={{ width: "220px" }}>Documents</th>
                                            <th className="text-center text-nowrap" style={{ width: "90px" }}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="12" className="text-center py-5">
                                                    <div
                                                        className="spinner-border text-primary"
                                                        role="status"
                                                    ></div>
                                                </td>
                                            </tr>
                                        ) : documents.length === 0 ? (
                                            <tr>
                                                <td colSpan="12" className="text-center py-5 text-muted">
                                                    No TDS records found.
                                                </td>
                                            </tr>
                                        ) : (
                                            documents.map((doc, index) => (
                                                <tr key={doc._id}>
                                                    <td>
                                                        {(pagination.currentPage - 1) * filters.limit +
                                                            index +
                                                            1}
                                                    </td>
                                                    <td>
                                                        {(() => {
                                                            const boqWlins = (doc.boqItems || []).filter(Boolean).map(item => item?.wlino).filter(Boolean);
                                                            if (boqWlins.length > 0) {
                                                                return (
                                                                    <div className="d-flex flex-wrap gap-1">
                                                                        {boqWlins.map((wlin, i) => (
                                                                            <Badge key={i} bg="secondary" className="rounded-pill">
                                                                                {wlin}
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                );
                                                            }
                                                            if (doc.wlino && typeof doc.wlino === "string" && doc.wlino.trim() !== "") {
                                                                const wlinList = doc.wlino.split(",").map(w => w.trim()).filter(Boolean);
                                                                if (wlinList.length > 0) {
                                                                    return (
                                                                        <div className="d-flex flex-wrap gap-1">
                                                                            {wlinList.map((wlin, i) => (
                                                                                <Badge key={i} bg="secondary" className="rounded-pill">
                                                                                    {wlin}
                                                                                </Badge>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                }
                                                            }
                                                            return <span className="text-muted">—</span>;
                                                        })()}
                                                    </td>
                                                    <td>
                                                        <div className="font-w600" style={{
                                                            maxHeight: "120px",
                                                            overflowY: "auto",
                                                            paddingRight: "5px",
                                                            scrollbarWidth: "thin"
                                                        }}>
                                                            {doc.productName?.includes(" / ") ? (
                                                                <ul className="ps-3 mb-0">
                                                                    {doc.productName.split(" / ").map((name, i) => (
                                                                        <li key={i}>{name}</li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                doc.productName
                                                            )}
                                                        </div>
                                                        {doc.documents?.some(d => d.remarks) && (
                                                            <div className="mt-1 small text-muted border-top pt-1">
                                                                {doc.documents.map((d, i) => d.remarks ? (
                                                                    <div key={i} className="text-truncate" style={{ maxWidth: "250px" }} title={d.remarks}>
                                                                        • {d.remarks}
                                                                    </div>
                                                                ) : null)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div>{doc.workOrder?.woNo || "—"}</div>
                                                        {doc.purchaseOrder?.poNo && (
                                                            <div className="text-muted small">
                                                                PO: {doc.purchaseOrder.poNo}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{doc.quom || "—"}</td>
                                                    <td>{doc.qty}</td>
                                                    <td>{formatDate(doc.submittedDate)}</td>
                                                    <td>{formatDate(doc.approvedDate)}</td>
                                                    <td>
                                                        <Badge
                                                            className="rounded-pill"
                                                            bg={
                                                                getApprovalDuration(
                                                                    doc.submittedDate,
                                                                    doc.approvedDate,
                                                                ) === "Pending"
                                                                    ? "warning"
                                                                    : "info"
                                                            }
                                                        >
                                                            {getApprovalDuration(
                                                                doc.submittedDate,
                                                                doc.approvedDate,
                                                            )}
                                                        </Badge>
                                                    </td>

                                                    <td>
                                                        <Badge className="rounded-pill" bg={getStatusBadge(doc.status)}>
                                                            {doc.status}
                                                        </Badge>
                                                        {doc.approvedBy?.name && (
                                                            <div className="small mt-1 text-muted">
                                                                By: {doc.approvedBy.name}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column gap-2">
                                                            {/* TDS Documents */}
                                                            {doc.documents?.some(d => d.docType === "TDS") && (
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <span className="badge badge-xs badge-info rounded-pill" style={{ width: "55px" }}>TDS</span>
                                                                    {doc.documents.filter(d => d.docType === "TDS").map((file, fIdx) => (
                                                                        <div key={fIdx} className="d-flex gap-1 align-items-center border rounded px-1 bg-light">
                                                                            <span className="small text-muted text-truncate" style={{ maxWidth: "80px" }} title={file.fileName}>
                                                                                {file.fileName}
                                                                            </span>
                                                                            <a
                                                                                href={getFileUrl(file)}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="btn btn-primary btn-xxs shadow"
                                                                                title={`Preview ${file.fileName}`}
                                                                            >
                                                                                <i className="fas fa-eye"></i>
                                                                            </a>
                                                                            <a
                                                                                href={getFileUrl(file)}
                                                                                download={file.fileName}
                                                                                className="btn btn-success btn-xxs shadow"
                                                                                title={`Download ${file.fileName}`}
                                                                            >
                                                                                <i className="fas fa-download"></i>
                                                                            </a>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Manual Documents */}
                                                            {doc.documents?.some(d => d.docType === "Manual" || d.docType === "Module") && (
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <span className="badge badge-xs badge-secondary rounded-pill" style={{ width: "55px" }}>MANUAL</span>
                                                                    {doc.documents.filter(d => d.docType === "Manual" || d.docType === "Module").map((file, fIdx) => (
                                                                        <div key={fIdx} className="d-flex gap-1 align-items-center border rounded px-1 bg-light">
                                                                            <span className="small text-muted text-truncate" style={{ maxWidth: "80px" }} title={file.fileName}>
                                                                                {file.fileName}
                                                                            </span>
                                                                            <a
                                                                                href={getFileUrl(file)}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="btn btn-primary btn-xxs shadow"
                                                                                title={`Preview ${file.fileName}`}
                                                                            >
                                                                                <i className="fas fa-eye"></i>
                                                                            </a>
                                                                            <a
                                                                                href={getFileUrl(file)}
                                                                                download={file.fileName}
                                                                                className="btn btn-success btn-xxs shadow"
                                                                                title={`Download ${file.fileName}`}
                                                                            >
                                                                                <i className="fas fa-download"></i>
                                                                            </a>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Legacy / Undefined Documents */}
                                                            {doc.documents?.filter(d => !d.docType).length > 0 && (
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <span className="badge badge-xs badge-secondary rounded-pill" style={{ width: "55px" }}>DOC</span>
                                                                    {doc.documents.filter(d => !d.docType).map((file, fIdx) => (
                                                                        <div key={fIdx} className="d-flex gap-1 align-items-center border rounded px-1 bg-light">
                                                                            <span className="small text-muted text-truncate" style={{ maxWidth: "80px" }} title={file.fileName}>
                                                                                {file.fileName}
                                                                            </span>
                                                                            <a
                                                                                href={getFileUrl(file)}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="btn btn-primary btn-xxs shadow"
                                                                                title={`Preview ${file.fileName}`}
                                                                            >
                                                                                <i className="fas fa-eye"></i>
                                                                            </a>
                                                                            <a
                                                                                href={getFileUrl(file)}
                                                                                download={file.fileName}
                                                                                className="btn btn-success btn-xxs shadow"
                                                                                title={`Download ${file.fileName}`}
                                                                            >
                                                                                <i className="fas fa-download"></i>
                                                                            </a>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <Dropdown>
                                                            <Dropdown.Toggle
                                                                as="div"
                                                                className="btn btn-link c-pointer i-false"
                                                            >
                                                                {svgMenu}
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu as={PortalMenu} align="end">
                                                                <PermissionWrapper user={user} required={["read:documents"]}>
                                                                    <Dropdown.Item
                                                                        onClick={() => {
                                                                            setSelectedDoc(doc);
                                                                            setShowModal(true);
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-eye me-2 text-primary"></i>{" "}
                                                                        View Details
                                                                    </Dropdown.Item>
                                                                </PermissionWrapper>

                                                                <PermissionWrapper user={user} required={["edit:documents"]}>
                                                                    <Dropdown.Item
                                                                        as={Link}
                                                                        to={`/tds/edit/${doc._id}${getListQueryString()}`}
                                                                    >
                                                                        <i className="fas fa-edit me-2 text-info"></i>{" "}
                                                                        Edit
                                                                    </Dropdown.Item>
                                                                </PermissionWrapper>

                                                                {doc.status === "Pending Approval" && (
                                                                    <>
                                                                        <PermissionWrapper user={user} required={["approve:documents"]}>
                                                                            <Dropdown.Item
                                                                                onClick={() =>
                                                                                    handleStatusChange(doc._id, "Approved")
                                                                                }
                                                                                className="text-success"
                                                                            >
                                                                                <i className="fas fa-check-circle me-2"></i>
                                                                                Approve
                                                                            </Dropdown.Item>
                                                                        </PermissionWrapper>

                                                                        <PermissionWrapper user={user} required={["reject:documents"]}>
                                                                            <Dropdown.Item
                                                                                onClick={() =>
                                                                                    handleStatusChange(doc._id, "Rejected")
                                                                                }
                                                                                className="text-danger"
                                                                            >
                                                                                <i className="fas fa-times-circle me-2"></i>
                                                                                Reject
                                                                            </Dropdown.Item>
                                                                        </PermissionWrapper>
                                                                    </>
                                                                )}
                                                                <Dropdown.Divider />
                                                                <PermissionWrapper user={user} required={["delete:documents"]}>
                                                                    <Dropdown.Item
                                                                        onClick={() => handleDelete(doc._id)}
                                                                        className="text-danger"
                                                                    >
                                                                        <i className="fas fa-trash me-2"></i> Delete
                                                                    </Dropdown.Item>
                                                                </PermissionWrapper>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        <AppPagination
                            currentPage={pagination.currentPage}
                            totalPages={pagination.totalPages}
                            totalCount={pagination.totalCount}
                            itemsLength={documents.length}
                            limit={filters.limit}
                            onPageChange={handlePageChange}
                        />
                    </Card>
                </Col>
            </Row>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>TDS Document Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedDoc && (
                        <div className="p-2">
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0 text-primary">Document Information</h5>
                                        <Badge
                                            bg={getStatusBadge(selectedDoc.status)}
                                            className="rounded-pill px-3 py-2"
                                        >
                                            {selectedDoc.status}
                                        </Badge>
                                    </div>
                                    <div className="table-responsive">
                                        <Table bordered size="sm">
                                            <tbody>
                                                <tr>
                                                    <td
                                                        className="bg-light font-w600"
                                                        style={{ width: "30%" }}
                                                    >
                                                        Work Order
                                                    </td>
                                                    <td>{selectedDoc.workOrder?.woNo || "—"}</td>
                                                </tr>
                                                {selectedDoc.purchaseOrder && (
                                                    <tr>
                                                        <td className="bg-light font-w600">
                                                            Purchase Order
                                                        </td>
                                                        <td>{selectedDoc.purchaseOrder.poNo}</td>
                                                    </tr>
                                                )}
                                                <tr>
                                                    <td className="bg-light font-w600">Product(s)</td>
                                                    <td>
                                                        {selectedDoc.productName?.includes(" / ") ? (
                                                            <ul className="ps-3 mb-0">
                                                                {selectedDoc.productName
                                                                    .split(" / ")
                                                                    .map((name, i) => (
                                                                        <li key={i}>{name}</li>
                                                                    ))}
                                                            </ul>
                                                        ) : (
                                                            selectedDoc.productName
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="bg-light font-w600">Total QTY</td>
                                                    <td>
                                                        {selectedDoc.qty} {selectedDoc.quom}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="bg-light font-w600">Dates</td>
                                                    <td>
                                                        <div className="small text-muted">
                                                            Submitted: {formatDate(selectedDoc.submittedDate)}
                                                        </div>
                                                        {selectedDoc.approvedDate && (
                                                            <div className="small text-success">
                                                                Approved: {formatDate(selectedDoc.approvedDate)}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                            </Row>

                            <h5 className="text-primary mb-3">Attached Files</h5>
                            <div className="list-group list-group-flush border rounded mb-3">
                                {(selectedDoc.documents?.length
                                    ? selectedDoc.documents
                                    : selectedDoc.document?.fileUrl
                                        ? [selectedDoc.document]
                                        : []
                                ).map((file, index) => (
                                    <div
                                        key={`${file.fileUrl}-${index}`}
                                        className="list-group-item d-flex justify-content-between align-items-center py-3"
                                    >
                                        <div className="d-flex align-items-center">
                                            <div className="icon-box bg-primary-light text-primary me-3 rounded">
                                                <i className="fas fa-file-pdf fa-lg"></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-0 text-dark" title={file.docType === "Module" ? "Manual" : (file.docType || "TDS")}>
                                                    {`Document ${index + 1}`}
                                                </h6>
                                                <small className="text-muted" title={file.docType === "Module" ? "Manual" : (file.docType || "TDS")}>{file.fileName}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <a
                                                href={getFileUrl(file)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="btn btn-outline-primary btn-sm rounded-circle"
                                            >
                                                <i className="fas fa-eye"></i>
                                            </a>
                                            <a
                                                href={getFileUrl(file)}
                                                download={file.fileName}
                                                className="btn btn-outline-success btn-sm rounded-circle"
                                            >
                                                <i className="fas fa-download"></i>
                                            </a>
                                            <button
                                                type="button"
                                                onClick={() => handleDocumentDelete(selectedDoc._id, index)}
                                                className="btn btn-outline-danger btn-sm rounded-circle"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                {selectedDoc.signature?.fileUrl && (
                                    <div className="list-group-item d-flex justify-content-between align-items-center py-3">
                                        <div className="d-flex align-items-center">
                                            <div className="icon-box bg-warning-light text-warning me-3 rounded">
                                                <i className="fas fa-signature fa-lg"></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-0 text-dark">Signature</h6>
                                                <small className="text-muted">
                                                    {selectedDoc.signature.fileName ||
                                                        "Captured via Canvas"}
                                                </small>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <a
                                                href={
                                                    selectedDoc.signature.fileUrl.startsWith("http")
                                                        ? selectedDoc.signature.fileUrl
                                                        : getFileUrl(selectedDoc.signature.fileUrl)
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                className="btn btn-outline-warning btn-sm rounded-circle"
                                            >
                                                <i className="fas fa-eye"></i>
                                            </a>
                                        </div>
                                    </div>
                                )}

                                {selectedDoc.stamp?.fileUrl && (
                                    <div className="list-group-item d-flex justify-content-between align-items-center py-3">
                                        <div className="d-flex align-items-center">
                                            <div className="icon-box bg-info-light text-info me-3 rounded">
                                                <i className="fas fa-stamp fa-lg"></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-0 text-dark">Stamp</h6>
                                                <small className="text-muted">
                                                    {selectedDoc.stamp.fileName}
                                                </small>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <a
                                                href={
                                                    selectedDoc.stamp.fileUrl.startsWith("http")
                                                        ? selectedDoc.stamp.fileUrl
                                                        : getFileUrl(selectedDoc.stamp.fileUrl)
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                className="btn btn-outline-info btn-sm rounded-circle"
                                            >
                                                <i className="fas fa-eye"></i>
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {selectedDoc.remarks && (
                                <div className="mt-3">
                                    <h6 className="font-w600 text-dark">Remarks:</h6>
                                    <p className="text-muted mb-0">{selectedDoc.remarks}</p>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        as={Link}
                        to={`/tds/edit/${selectedDoc?._id}${getListQueryString()}`}
                    >
                        Edit TDS
                    </Button>
                </Modal.Footer>
            </Modal>

            <SignatureModal
                show={showSignModal}
                onHide={() => setShowSignModal(false)}
                onSave={handleSignatureSave}
                title="Capture Signature for TDS Approval"
                showStampUpload={true}
            />
        </Fragment>
    );
};

export default TDSList;
